import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { StatusBadge, type StatusType } from '@/components/ui/status-badge';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  TableLoading,
} from '@/components/ui/table';
import { usePortOrders, usePortOrderStore, PortOrderDrawer, NumberManifestDrawer } from '@/features/dids';
import { PortOrderModal } from '@/features/dids/components/port-order-modal';
import { isEmbedMode } from '@/lib/utils';
import {
  Plus,
  ArrowLeftRight,
  Search,
  ChevronRight,
} from 'lucide-react';
import type { PortOrder, PortOrderStatus } from '@/features/dids/types';

// Map port order status to StatusBadge type
function getStatusType(status: PortOrderStatus | null): StatusType {
  switch (status) {
    case 'COMPLETED':
      return 'completed';
    case 'REJECTED':
      return 'rejected';
    case 'CANCELLED':
      return 'inactive';
    case 'FOCd':
    case 'SUBMITTED':
    case 'PENDING':
    case 'NEW':
      return 'pending';
    default:
      return 'pending';
  }
}

// Get status label for display
function getStatusLabel(status: PortOrderStatus | null): string {
  switch (status) {
    case 'COMPLETED':
      return 'Completed';
    case 'REJECTED':
      return 'Rejected';
    case 'CANCELLED':
      return 'Cancelled';
    case 'FOCd':
      return 'FOC Received';
    case 'SUBMITTED':
      return 'In Progress';
    case 'PENDING':
      return 'Pending';
    case 'NEW':
      return 'New';
    default:
      return 'Unknown';
  }
}

// Format date for display
function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

// DID Quantity Badge Component
interface DIDQuantityBadgeProps {
  count: number;
  onClick: (e: React.MouseEvent) => void;
}

function DIDQuantityBadge({ count, onClick }: DIDQuantityBadgeProps) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 rounded-md px-2 py-1 font-medium text-sm hover:bg-indigo-100 transition-colors"
    >
      {count} DIDs
      <ChevronRight size={14} className="text-indigo-400" />
    </button>
  );
}

// Port Order Row Component
interface PortOrderRowProps {
  order: PortOrder;
  onRowClick: () => void;
  onQuantityClick: (e: React.MouseEvent) => void;
}

function PortOrderRow({ order, onRowClick, onQuantityClick }: PortOrderRowProps) {
  return (
    <TableRow
      className="cursor-pointer hover:bg-slate-50/80 transition-colors"
      onClick={onRowClick}
    >
      {/* ORDER ID */}
      <TableCell className="px-6">
        <span className="font-mono text-indigo-600 font-medium">
          #PORT-{String(order.id).padStart(5, '0')}
        </span>
      </TableCell>

      {/* LOSING CARRIER */}
      <TableCell className="px-4">
        <span className="text-slate-700">{order.porting_from || '-'}</span>
      </TableCell>

      {/* STATUS */}
      <TableCell className="px-4">
        <StatusBadge
          status={getStatusType(order.status)}
          label={getStatusLabel(order.status)}
        />
      </TableCell>

      {/* QUANTITY */}
      <TableCell className="px-4">
        <DIDQuantityBadge
          count={order.did_count || order.dids?.length || 0}
          onClick={onQuantityClick}
        />
      </TableCell>

      {/* SUBMITTED */}
      <TableCell className="px-4">
        <span className="text-sm text-slate-500">{formatDate(order.submitted_date)}</span>
      </TableCell>

      {/* EST. COMPLETION */}
      <TableCell className="px-4">
        <span className="text-sm text-slate-500">{formatDate(order.foc_date)}</span>
      </TableCell>
    </TableRow>
  );
}

export function PortingPage() {
  const isEmbed = isEmbedMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPortOrderModal, setShowPortOrderModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: portOrders = [], isLoading, error } = usePortOrders();
  const { openDrawer, openManifest } = usePortOrderStore();

  // Handle deep-linking: ?order=XXX&drawer=timeline opens port order drawer
  useEffect(() => {
    const orderParam = searchParams.get('order');
    const drawerParam = searchParams.get('drawer');

    if (orderParam && drawerParam === 'timeline') {
      const orderId = parseInt(orderParam, 10);
      if (!isNaN(orderId)) {
        openDrawer(orderId);
        // Clear params so drawer can be closed
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, openDrawer]);

  // Filter orders by search query
  const filteredOrders = portOrders.filter((order) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const orderId = `#PORT-${String(order.id).padStart(5, '0')}`.toLowerCase();
    return (
      orderId.includes(query) ||
      order.porting_from?.toLowerCase().includes(query) ||
      order.status?.toLowerCase().includes(query) ||
      order.sales_rep?.toLowerCase().includes(query)
    );
  });

  // Paginate
  const totalItems = filteredOrders.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Calculate metrics for header
  const activeOrders = portOrders.filter(
    (o) => !['COMPLETED', 'CANCELLED', 'REJECTED'].includes(o.status || '')
  ).length;
  const completedOrders = portOrders.filter((o) => o.status === 'COMPLETED').length;

  // Handle clicking the quantity badge
  const handleQuantityClick = (order: PortOrder, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    openManifest(order.id, order.dids || []);
  };

  return (
    <div>
      <PageHeader title="Porting Orders" />

      <div className="p-6">
        {/* Section Header with Metrics */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Port Order Management</h2>
            <p className="text-sm text-slate-500 mt-1">
              {activeOrders} active orders · {completedOrders} completed
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!isEmbed && (
              <Button
                onClick={() => setShowPortOrderModal(true)}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 text-white shadow-indigo"
              >
                <Plus size={16} className="mr-2" />
                New Port Request
              </Button>
            )}
          </div>
        </div>

        {/* Port Orders Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Search */}
          <div className="px-6 py-4 border-b border-slate-100">
            <div className="relative max-w-sm">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-6 w-48">ORDER ID</TableHead>
                <TableHead className="px-4 w-48">LOSING CARRIER</TableHead>
                <TableHead className="px-4 w-36">STATUS</TableHead>
                <TableHead className="px-4 w-28">QUANTITY</TableHead>
                <TableHead className="px-4 w-36">SUBMITTED</TableHead>
                <TableHead className="px-4 w-40">EST. COMPLETION</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableLoading colSpan={6} rows={5} />
              ) : error ? (
                <TableEmpty
                  colSpan={6}
                  message="Error loading port orders. Please try again."
                  icon={<ArrowLeftRight className="h-8 w-8 text-red-300" />}
                />
              ) : paginatedOrders.length === 0 ? (
                <TableEmpty
                  colSpan={6}
                  message={
                    searchQuery
                      ? 'No orders match your search'
                      : 'No port orders yet. Start a new port request to begin.'
                  }
                  icon={<ArrowLeftRight className="h-8 w-8 text-slate-300" />}
                />
              ) : (
                paginatedOrders.map((order) => (
                  <PortOrderRow
                    key={order.id}
                    order={order}
                    onRowClick={() => openDrawer(order.id)}
                    onQuantityClick={(e) => handleQuantityClick(order, e)}
                  />
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            className="border-t border-slate-100"
          />
        </div>
      </div>

      {/* Port Order Timeline Drawer */}
      <PortOrderDrawer />

      {/* Number Manifest Drawer */}
      <NumberManifestDrawer />

      {/* Port Order Modal (for new port requests) */}
      <PortOrderModal
        isOpen={showPortOrderModal}
        onClose={() => setShowPortOrderModal(false)}
      />
    </div>
  );
}
